import Observable from 'zen-observable'
import Vue from 'vue'
export { merge, combineLatest } from 'zen-observable/extras'
export default Observable

Object.defineProperty(Observable.prototype, 'debounce', {
  value(timeoutDelay, { leading = false } = {}) {
    return new Observable(observer => {
      let nextValue
      let hasNextValue = false
      let timeoutId = null

      function stop() {
        clearTimeout(timeoutId)
      }

      function start() {
        timeoutId = setTimeout(() => {
          timeoutId = null
          if (hasNextValue) {
            observer.next(nextValue)
            hasNextValue = false
            nextValue = undefined
            if (leading) {
              // If we emited the leading value, restart the timer to ignore the next value during
              // the timeoutDelay
              start()
            }
          }
        }, timeoutDelay)
      }

      const subscription = this.subscribe(
        value => {
          // If the timer isn't active yet
          if (timeoutId === null) {
            if (leading) {
              // If we need to emit the leading value, emit it
              observer.next(value)
            } else {
              // Else emit the value when the timer finishes
              nextValue = value
              hasNextValue = true
            }

            // Start the timer
            start()
          } else {
            // Emit the value when the timer finishes
            nextValue = value
            hasNextValue = true
          }
        },
        error => {
          observer.error(error)
          stop()
        },
        () => {
          observer.complete()
          stop()
        },
      )

      return () => {
        subscription.unsubscribe()
        stop()
      }
    })
  },
})

// https://rxjs.dev/api/operators/switchMap
Object.defineProperty(Observable.prototype, 'switchMap', {
  value(fn) {
    return new Observable(observer => {
      let inner
      const outer = this.subscribe(
        value => {
          if (inner) {
            inner.unsubscribe()
            inner = null
          }
          if (fn) {
            try {
              value = fn(value)
            } catch (e) {
              return observer.error(e)
            }
          }
          inner = Observable.from(value).subscribe(
            value => observer.next(value),
            error => observer.error(error),
            () => {
              inner = null
              completeIfDone()
            },
          )
        },
        error => observer.error(error),
        () => completeIfDone(),
      )

      function completeIfDone() {
        if (outer.closed && !inner) observer.complete()
      }

      return () => {
        if (inner) inner.unsubscribe()
        outer.unsubscribe()
      }
    })
  },
})

export function observableFromVueWatch(fn, options) {
  return new Observable(observer => {
    const vm = new Vue()
    return vm.$watch(fn, next => observer.next(next), options)
  })
}
